import { AlertCircle, ArrowDown, Info } from "lucide-react";
import { useEffect, useRef } from "react";

import {
  formatSendMoney,
  formatSendMoneyWithCode,
} from "@/modules/dashboard/dispersions/components/WalletSend/formatSendMoney";
import { QuoteValidity } from "@/modules/dashboard/dispersions/components/WalletSend/QuoteValidity";
import type { NativeQuote } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { formatRateLine } from "@/modules/dashboard/dispersions/utils/walletSend";
import { CurrencyFlag } from "@/modules/dashboard/wallets/components/CurrencyFlag";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { compareDecimalStrings } from "@/shared/utils/decimal";

import { type SwapBreakdown, swapBreakdown, swapReadableRate } from "../../utils/walletSwap";

export interface SwapReviewProps {
  quote: NativeQuote;
  /** The figures when the review opened; the price is renewed silently, so a change is said out loud. */
  reviewed: SwapBreakdown | null;
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right font-mono text-sm text-foreground tabular-nums ${strong ? "font-bold" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

/** What leaves, what lands and at what price, before the conversion is made. */
export function SwapReview({ quote, reviewed }: SwapReviewProps) {
  const { t, activeLanguage } = useTranslations("swaps");
  const headingRef = useRef<HTMLParagraphElement>(null);
  const breakdown = swapBreakdown(quote);
  const rate = swapReadableRate(quote);
  const from = quote.currency.from;
  const to = quote.currency.to;
  const free = compareDecimalStrings(breakdown.fee, "0") <= 0;
  const changed =
    !!reviewed &&
    (compareDecimalStrings(reviewed.debit, breakdown.debit) !== 0 ||
      compareDecimalStrings(reviewed.credit, breakdown.credit) !== 0);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="space-y-4">
      {changed && <ColocaAlert variant="warning" icon={AlertCircle} text={t("convert_review_changed")} />}
      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="px-5 pt-5 pb-4">
          <p
            ref={headingRef}
            tabIndex={-1}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground outline-none"
          >
            <CurrencyFlag currency={from} />
            {t("convert_review_leaves", { currency: from })}
          </p>
          <p className="mt-1.5 font-mono text-2xl font-bold tracking-tight text-foreground tabular-nums">
            {formatSendMoney(breakdown.debit, from, activeLanguage)}
            <span className="ml-2 text-sm font-medium tracking-wider text-muted-foreground">{from}</span>
          </p>
        </div>
        <div className="relative flex items-center px-5">
          <span className="h-px flex-1 border-t border-dashed border-border" />
          <span className="mx-3 flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ArrowDown className="size-4" aria-hidden="true" />
          </span>
          <span className="h-px flex-1 border-t border-dashed border-border" />
        </div>
        <div className="bg-primary/5 px-5 pt-4 pb-5">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <CurrencyFlag currency={to} />
            {t("convert_review_lands", { currency: to })}
          </p>
          <p className="mt-1.5 font-mono text-3xl font-bold tracking-tight text-foreground tabular-nums">
            {formatSendMoney(breakdown.credit, to, activeLanguage)}
            <span className="ml-2 text-sm font-medium tracking-wider text-muted-foreground">{to}</span>
          </p>
        </div>
        <dl className="border-t border-border bg-muted/30 px-5 py-3">
          {rate && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-1.5">
              <dt className="text-sm text-muted-foreground">{t("convert_rate")}</dt>
              <dd className="flex flex-col items-end gap-0.5">
                <span className="font-mono text-sm text-foreground tabular-nums">
                  {formatRateLine(rate, activeLanguage)}
                </span>
                <QuoteValidity expiresAt={quote.expires_at} />
              </dd>
            </div>
          )}
          <Row
            label={t("convert_row_fee")}
            value={
              free ? t("convert_fee_free") : formatSendMoneyWithCode(breakdown.fee, from, activeLanguage)
            }
          />
        </dl>
      </section>
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        {t("convert_review_irreversible")}
      </p>
    </div>
  );
}
